import type {
  LevelEntry,
  PickupPlacement,
  PickupType,
  SegmentTrick,
  Shape,
  ShapeSegment,
  GateSegment,
} from "./types";
import { BOSS_PHRASES, bossEntriesByIndex } from "./bosses";
import type { DailyModifierId } from "./dailyModifiers";
import type { DifficultyProfile } from "../config/difficulty";

export type RngFn = () => number;

interface PhraseTemplate {
  shape: Shape;
  count: number;
  // Lengths and gap pools per "step" in the phrase. The generator
  // randomly mutates lengths within +/- LENGTH_JITTER and picks gaps from
  // the pool with probability that grows with chunkIndex.
  baseLength: number;
  gapPool: number[][];
}

const PHRASES_BY_SHAPE: Record<Shape, PhraseTemplate[]> = {
  octagon: [
    {
      shape: "octagon",
      count: 3,
      baseLength: 56,
      gapPool: [[3], [2, 6], [4], [1, 5], [3, 7]],
    },
    {
      shape: "octagon",
      count: 4,
      baseLength: 48,
      gapPool: [[2], [4], [1, 5], [0, 3, 6], [2, 4, 7]],
    },
  ],
  square: [
    {
      shape: "square",
      count: 3,
      baseLength: 38,
      gapPool: [[1], [3], [0, 2], [1, 3]],
    },
    {
      shape: "square",
      count: 4,
      baseLength: 32,
      gapPool: [[0], [2], [1, 3], [0, 2]],
    },
  ],
  triangle: [
    {
      shape: "triangle",
      count: 3,
      baseLength: 42,
      gapPool: [[0], [1], [2], [0, 2]],
    },
    {
      shape: "triangle",
      count: 4,
      baseLength: 36,
      gapPool: [[1], [2], [0], [1, 2]],
    },
  ],
  // Pentagon -- 5 walls, gap pool stays modest because removing more
  // than two walls leaves the player only two safe panels to land on.
  pentagon: [
    {
      shape: "pentagon",
      count: 3,
      baseLength: 44,
      gapPool: [[0], [2], [4], [1, 3], [0, 2]],
    },
    {
      shape: "pentagon",
      count: 4,
      baseLength: 36,
      gapPool: [[1], [3], [0, 3], [2, 4]],
    },
  ],
  // Hexagon -- 6 walls, comfortable middle ground; can support
  // triple-gap layouts comfortably for advanced phrases.
  hexagon: [
    {
      shape: "hexagon",
      count: 3,
      baseLength: 50,
      gapPool: [[1], [3], [0, 3], [2, 5], [1, 4]],
    },
    {
      shape: "hexagon",
      count: 4,
      baseLength: 42,
      gapPool: [[0], [2], [4], [0, 3], [1, 4], [0, 2, 4]],
    },
  ],
};

// Shape rotation order. We don't include all shapes back-to-back --
// pentagon and hexagon are interleaved between the originals so each
// trip through the cycle sees a mix of familiar + novel polygon counts.
const SHAPE_CYCLE: Shape[] = [
  "octagon",
  "square",
  "hexagon",
  "triangle",
  "pentagon",
];
const SIDES_OF: Record<Shape, number> = {
  octagon: 8,
  square: 4,
  triangle: 3,
  pentagon: 5,
  hexagon: 6,
};

const LENGTH_JITTER = 0.15;       // +/- 15% on base length
const SPEED_FLOOR = 14;
const SPEED_CEILING = 32;
const SPEED_RAMP_CHUNKS = 60;     // chunks to reach ceiling
const TRIPLE_GAP_BIAS_CHUNKS = 20; // earlier chunks skew toward simpler gaps

// Pickup spawn tuning. Score orbs are forgiving; risk pickups are
// gated behind a few warmup chunks so new players don't see them in
// the first few seconds and immediately fall.
const SCORE_PROB = 0.3;
const RISK_PROB = 0.12;
const RISK_WARMUP_CHUNKS = 4;
const MIN_RISK_Z = 0.35;

// Power pickups: magnet + double-score. Both spawn on safe panels
// (not gaps) so they're "free reward" rather than risk-reward. The
// effective warmup is computed inside maybeMakePickups() because it
// rides on the difficulty-adjusted RISK warmup (so Easy delays them,
// Hard front-loads them).
const POWER_PROB_EACH = 0.03;

// Trick segments (pulse / mirror / sweep) start showing up after the
// player has had a few "normal" chunks to learn the rhythm. After
// warmup, ~18% of each generated SHAPE segment gets a random trick
// applied. We deliberately keep multi-trick clustering low to avoid
// overwhelming the player; one trick per phrase is the common case.
const TRICK_PROB = 0.18;
const TRICK_WARMUP_CHUNKS = 6;
const TRICKS: SegmentTrick[] = ["pulse", "mirror", "sweep"];

// Boss insertion cadence. Every BOSS_EVERY chunks the next next()
// returns a boss phrase (round-robin from BOSS_PHRASES) instead of a
// regular phrase, then resets the counter.
const BOSS_EVERY = 12;
// Boss-rush modifier shortens this dramatically. We override at
// construction time when modifier === "boss_rush".
const BOSS_EVERY_RUSH = 3;

// Banking cadence. After warmup, BANK_PROB of regular shape segments
// get a non-null bank value. We track the previous bank so we never
// emit two same-bank segments back-to-back -- alternating left/right
// keeps the camera roll feeling like cornering, not "permanently
// drunk".
const BANK_PROB = 0.12;
const BANK_WARMUP_CHUNKS = 8;

// Procedural endless level producer. Emits "phrases" — small chunks of
// LevelEntry that always end on a gate so that subsequent chunks can
// safely change shape without surprising the player. The generator keeps
// running difficulty curves on speed and gap density indexed off
// chunkIndex; an optional seeded RNG makes Daily Run deterministic.
export class EndlessGenerator {
  private rng: RngFn;
  private chunkIndex = 0;
  private nextShapeIdx = 0;
  // Counts chunks since the last boss phrase so we can insert a boss
  // every BOSS_EVERY chunks. Round-robined via nextBossIdx so a long
  // run cycles through all four authored bosses.
  private chunksSinceBoss = 0;
  private nextBossIdx = 0;
  // How many shape segments have been emitted total. Used to skip
  // forced-trick application on the very first segment so sweep_day
  // dailies don't drop a kill-bar onto the player's spawn panel
  // before snare beats have had a chance to move it.
  private segmentsEmitted = 0;
  // Tracks the most recent emitted bank so we never produce two
  // same-bank segments back-to-back. 0 = no bank on the last segment.
  private lastBank: -1 | 0 | 1 = 0;
  // Active daily modifier (null in story / endless / non-modifier
  // daily runs). Drives the post-processing in next() that rewrites
  // tricks, boss cadence, and speed.
  private modifier: DailyModifierId | null = null;
  // Cached overrides derived from the modifier so next() doesn't have
  // to re-switch on the modifier id every call.
  private bossEvery = BOSS_EVERY;
  private speedMul = 1;
  private forcedTrick: SegmentTrick | null = null;
  // Difficulty profile drives long-lived multipliers for speed,
  // pickup probabilities, trick density, boss cadence, and the
  // post-warmup chunk thresholds. Defaults to "no-op" so legacy
  // callers (tests) work without supplying a profile.
  private difficulty: DifficultyProfile | null = null;

  constructor(
    rng: RngFn = Math.random,
    modifier: DailyModifierId | null = null,
    difficulty: DifficultyProfile | null = null
  ) {
    this.rng = rng;
    this.modifier = modifier;
    this.difficulty = difficulty;
    this.applyModifier();
  }

  reset(): void {
    this.chunkIndex = 0;
    this.nextShapeIdx = 0;
    this.chunksSinceBoss = 0;
    this.nextBossIdx = 0;
    this.lastBank = 0;
    this.segmentsEmitted = 0;
  }

  // Translate the active modifier into the cached overrides we use
  // later in next(). Kept out of the per-segment hot path.
  // Difficulty profile multipliers compose with modifier overrides:
  //   bossEvery = round(BOSS_EVERY * bossEveryMul) for normal play,
  //               or BOSS_EVERY_RUSH (modifier wins) for boss_rush.
  //   speedMul  = (modifier speedMul) * (difficulty speedMul).
  private applyModifier(): void {
    const diffSpeed = this.difficulty?.speedMul ?? 1;
    const diffBossMul = this.difficulty?.bossEveryMul ?? 1;
    this.bossEvery = Math.max(2, Math.round(BOSS_EVERY * diffBossMul));
    this.speedMul = diffSpeed;
    this.forcedTrick = null;
    if (!this.modifier) return;
    switch (this.modifier) {
      case "mirror_day":
        this.forcedTrick = "mirror";
        break;
      case "pulse_day":
        this.forcedTrick = "pulse";
        break;
      case "sweep_day":
        this.forcedTrick = "sweep";
        break;
      case "boss_rush":
        // Boss-rush modifier overrides the difficulty cadence -- it's
        // the player's explicit "I want bosses NOW" pick for the day.
        this.bossEvery = BOSS_EVERY_RUSH;
        break;
      case "speed_demon":
        this.speedMul = 1.3 * diffSpeed;
        break;
      // sub_only and shield_off are handled outside the generator
      // (MusicDirector / PickupManager). Generator output is unchanged
      // for those modifiers.
      case "sub_only":
      case "shield_off":
        break;
    }
  }

  // Produce ~6-8 entries: a phrase of segments in one shape, capped by a
  // gate to whatever shape the next phrase will use. Periodically
  // returns a boss phrase instead.
  next(): LevelEntry[] {
    if (this.chunksSinceBoss >= this.bossEvery) {
      const entries = bossEntriesByIndex(this.nextBossIdx);
      this.nextBossIdx = (this.nextBossIdx + 1) % BOSS_PHRASES.length;
      this.chunksSinceBoss = 0;
      this.chunkIndex += 1;
      // Boss phrases end on a gate already (see bosses.ts), so the
      // streamer's "phrases end on a gate" invariant is preserved.
      return entries;
    }

    const shape = SHAPE_CYCLE[this.nextShapeIdx % SHAPE_CYCLE.length]!;
    const nextShape =
      SHAPE_CYCLE[(this.nextShapeIdx + 1) % SHAPE_CYCLE.length]!;
    this.nextShapeIdx += 1;

    const templates = PHRASES_BY_SHAPE[shape];
    const tpl = templates[Math.floor(this.rng() * templates.length)]!;

    const speed = this.speedForChunk();
    const out: LevelEntry[] = [];
    for (let i = 0; i < tpl.count; i++) {
      const seg = this.makeSegment(tpl, speed + i * 0.3);
      this.maybeApplyTrick(seg);
      this.maybeApplyBank(seg);
      out.push(seg);
      this.segmentsEmitted += 1;
    }
    out.push(this.makeGate(nextShape));

    this.chunkIndex += 1;
    this.chunksSinceBoss += 1;
    return out;
  }

  // Sprinkle pulse/mirror/sweep onto a fraction of regular segments
  // after the warmup period. Picks a uniform-random trick when it
  // applies. Sweeps need a non-trivial gap layout to be readable, so
  // for sweep we skip if the segment has 0 gaps (would be impossible
  // to dodge at speed); pulse/mirror are fine on any gap layout.
  //
  // When a daily modifier forces a trick, we apply it to every
  // segment regardless of warmup / probability. Sweep-day still
  // honours the gap requirement so we don't generate undodgeable bars.
  private maybeApplyTrick(seg: ShapeSegment): ShapeSegment {
    if (this.forcedTrick) {
      if (this.forcedTrick === "sweep" && seg.gaps.length === 0) return seg;
      // Skip the very first emitted segment so the player isn't
      // standing on top of a freshly-spawned trick effect on frame
      // one. Most relevant for sweep_day: the kill-bar spawns on
      // panel 0 (the spawn panel) and would CRASH the player before
      // they could rotate. mirror_day / pulse_day are also gated for
      // a uniform "first-segment is breathing room" feel.
      if (this.segmentsEmitted === 0) return seg;
      seg.trick = this.forcedTrick;
      return seg;
    }
    if (this.chunkIndex < TRICK_WARMUP_CHUNKS) return seg;
    const trickProb = TRICK_PROB * (this.difficulty?.trickProbMul ?? 1);
    if (this.rng() >= trickProb) return seg;
    const trick = TRICKS[Math.floor(this.rng() * TRICKS.length)]!;
    if (trick === "sweep" && seg.gaps.length === 0) return seg;
    seg.trick = trick;
    return seg;
  }

  // Sprinkle a sustained left/right bank onto a fraction of regular
  // segments after the warmup. We track the previous bank in
  // lastBank so we never emit two same-direction banks in a row --
  // alternating left/right gives the camera something interesting to
  // do without the player feeling like the world is permanently tilted.
  private maybeApplyBank(seg: ShapeSegment): void {
    if (this.chunkIndex < BANK_WARMUP_CHUNKS) {
      this.lastBank = 0;
      return;
    }
    if (this.rng() >= BANK_PROB) {
      this.lastBank = 0;
      return;
    }
    let bank: -1 | 1;
    if (this.lastBank === 1) bank = -1;
    else if (this.lastBank === -1) bank = 1;
    else bank = this.rng() < 0.5 ? -1 : 1;
    seg.bank = bank;
    this.lastBank = bank;
  }

  // Larger initial batch so the streamer has plenty of runway from frame
  // one. Returns ~3 phrases worth.
  initialBatch(): LevelEntry[] {
    const out: LevelEntry[] = [];
    for (let i = 0; i < 3; i++) {
      for (const e of this.next()) out.push(e);
    }
    return out;
  }

  private speedForChunk(): number {
    const t = Math.min(1, this.chunkIndex / SPEED_RAMP_CHUNKS);
    const base = SPEED_FLOOR + (SPEED_CEILING - SPEED_FLOOR) * t;
    // Speed-demon modifier multiplies the curve; clamp at the ceiling
    // so we don't end up scrolling past the BPM ramp's top.
    return Math.min(SPEED_CEILING * 1.3, base * this.speedMul);
  }

  private makeSegment(tpl: PhraseTemplate, speed: number): ShapeSegment {
    const jitter = 1 + (this.rng() * 2 - 1) * LENGTH_JITTER;
    const length = Math.max(20, Math.round(tpl.baseLength * jitter));
    // Earlier chunks bias toward simpler gap patterns (single-side).
    // Past TRIPLE_GAP_BIAS_CHUNKS, the full gap pool is fair game.
    const poolEnd = Math.min(
      tpl.gapPool.length,
      2 +
        Math.floor(
          (this.chunkIndex / TRIPLE_GAP_BIAS_CHUNKS) * tpl.gapPool.length
        )
    );
    const gapIdx = Math.floor(this.rng() * Math.max(1, poolEnd));
    const gaps = tpl.gapPool[gapIdx] ?? tpl.gapPool[0]!;
    const seg: ShapeSegment = {
      shape: tpl.shape,
      speed: Math.round(speed),
      length,
      gaps: [...gaps],
    };
    const pickups = this.maybeMakePickups(tpl.shape, gaps);
    if (pickups.length > 0) seg.pickups = pickups;
    return seg;
  }

  private maybeMakePickups(shape: Shape, gaps: number[]): PickupPlacement[] {
    const sides = SIDES_OF[shape];
    const out: PickupPlacement[] = [];
    const gapSet = new Set(gaps);
    // Build the set of safe panels (everything that's not a gap).
    const safePanels: number[] = [];
    for (let i = 0; i < sides; i++) {
      if (!gapSet.has(i)) safePanels.push(i);
    }
    const scoreProb = SCORE_PROB * (this.difficulty?.scoreProbMul ?? 1);
    const riskProb = RISK_PROB * (this.difficulty?.riskProbMul ?? 1);
    const riskWarmup = Math.max(
      0,
      RISK_WARMUP_CHUNKS + (this.difficulty?.riskWarmupBonus ?? 0)
    );
    const powerWarmup = riskWarmup + 4;

    if (safePanels.length > 0 && this.rng() < scoreProb) {
      const panel = safePanels[Math.floor(this.rng() * safePanels.length)]!;
      out.push({ type: "score", panel, z: 0.4 + this.rng() * 0.3 });
    }

    if (
      this.chunkIndex >= riskWarmup &&
      gaps.length > 0 &&
      this.rng() < riskProb
    ) {
      const panel = gaps[Math.floor(this.rng() * gaps.length)]!;
      // Risk-pickup type: 60% shield, 40% slow-mo. Shield is more
      // generally useful so it shows up more often.
      const type: PickupType = this.rng() < 0.6 ? "shield" : "slowmo";
      const z = MIN_RISK_Z + this.rng() * (0.85 - MIN_RISK_Z);
      out.push({ type, panel, z });
    }

    // Power pickups (magnet + double): independent rolls, placed on a
    // safe panel that wasn't already taken by the score orb above.
    // Either may spawn; both can spawn (rare ~0.1% segments) and
    // that's fine - one chip per HUD type so the player still reads
    // the layout cleanly. Difficulty modulates the same way as score
    // orbs (Easy gets more, Hard gets fewer).
    const powerProb =
      POWER_PROB_EACH * (this.difficulty?.scoreProbMul ?? 1);
    if (this.chunkIndex >= powerWarmup && safePanels.length > 0) {
      const tryPower = (type: "magnet" | "double") => {
        if (this.rng() >= powerProb) return;
        const panel = safePanels[Math.floor(this.rng() * safePanels.length)]!;
        out.push({ type, panel, z: 0.45 + this.rng() * 0.25 });
      };
      tryPower("magnet");
      tryPower("double");
    }

    return out;
  }

  private makeGate(toShape: Shape): GateSegment {
    return { shape: "gate", to: toShape };
  }
}
